import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import api from "../Utils/api";
import { boletimValidationError } from "../Utils/boletimValidation";
import {
  getDraft,
  getLatestDraft,
  getPendingDrafts,
  deleteDraft,
  migrateLegacyDraft,
  offlineDraftsEnabled,
  putDraftPreservingInFlight,
  markDraftInFlight,
  updateDraftAfterSync,
} from "../offline/draftStore";

const BoletimContext = createContext();

const novoBoletimVazio = () => ({
  municipio: "",
  natureza: "",
  naturezaId: "",
  naturezaIds: [],
  numero: "",
  data: "",
  horario: "",
  _id: null,
  clientRequestId: uuidv4(),
  documentRevision: 0,
  envolvidos: [],
  materiaisApreendidos: [],
  efetivo: [],
  historicohtml: "",
});

const currentUserId = () => localStorage.getItem("x-user-id");

function reportIsReady(report) {
  return !boletimValidationError(report);
}

function hasContent(report) {
  return Boolean(report?.numero || report?.data || report?.natureza || report?.historicohtml ||
    report?.envolvidos?.length || report?.materiaisApreendidos?.length || report?.efetivo?.length ||
    ['municipio', 'horario', 'endereco', 'numeroEndereco', 'bairro', 'referencia', 'latitude', 'longitude', 'localidadeId'].some(
      field => report?.[field] !== undefined && report?.[field] !== null && String(report[field]).trim() !== '') ||
    report?.formDrafts?.envolvido?.nome || report?.formDrafts?.envolvido?.cpf ||
    report?.formDrafts?.material?.item?.tipoItem || report?.formDrafts?.efetivo?.nome || report?.formDrafts?.efetivo?.id);
}

function isBlankDraft(report) {
  return !hasContent(report);
}

function isPermanentError(error) {
  return [400, 401, 403, 404, 409, 422].includes(error.response?.status);
}

function BoletimProvider({ children }) {
  const [boletim, setBoletimState] = useState(novoBoletimVazio);
  const [syncStatus, setSyncStatus] = useState({ state: "idle", message: "" });
  const [pendingCount, setPendingCount] = useState(0);
  const currentRef = useRef(boletim);
  const localIdRef = useRef(boletim.clientRequestId);
  const versionRef = useRef(0);
  const persistQueueRef = useRef(Promise.resolve());
  const debounceRef = useRef(null);
  const retryRef = useRef(null);
  const retryAttemptsRef = useRef(0);
  const syncPendingRef = useRef(() => Promise.resolve());
  const syncingRef = useRef(false);
  const savePromiseRef = useRef(null);
  const syncWaitersRef = useRef([]);
  const dirtyRef = useRef(false);
  const finalizationIntentRef = useRef(null);
  const mountedRef = useRef(true);

  const showStatus = useCallback((state, message = "") => {
    if (mountedRef.current) setSyncStatus({ state, message });
  }, []);

  const storeRecord = useCallback((report, status = "pending", version = versionRef.current) => ({
    localId: localIdRef.current,
    serverId: report._id || null,
    userId: currentUserId(),
    dados: report,
    status,
    versaoLocal: version,
    ultimaAlteracao: new Date().toISOString(),
    ultimaSincronizacao: status === "synced" ? report.updatedAt || new Date().toISOString() : null,
    tentativas: 0,
    erro: null,
    ...(finalizationIntentRef.current ? { finalizationRequestedAt: finalizationIntentRef.current } : {}),
    updatedAt: new Date().toISOString(),
  }), []);

  const persistLocally = useCallback((report, status = "pending", version = versionRef.current) => {
    if (!offlineDraftsEnabled || !currentUserId()) return Promise.resolve(null);
    const record = storeRecord(report, status, version);
    persistQueueRef.current = persistQueueRef.current
      .catch(() => undefined)
      .then(() => putDraftPreservingInFlight(record));
    return persistQueueRef.current;
  }, [storeRecord]);

  const applyServerReport = useCallback(async (report, localId, version, syncRequestId) => {
    let nextRecord = null;
    if (offlineDraftsEnabled) {
      nextRecord = await updateDraftAfterSync(localId, version, report, syncRequestId);
    }
    const stillSameDraft = localIdRef.current === localId;
    if (stillSameDraft) {
      const latest = currentRef.current;
      const hasNewerLocalChanges = versionRef.current !== version;
      const merged = hasNewerLocalChanges
        ? { ...latest, _id: report._id, documentRevision: report.documentRevision || 0, createdAt: report.createdAt, updatedAt: report.updatedAt }
        : { ...report, formDrafts: latest.formDrafts, clientRequestId: latest.clientRequestId || report.clientRequestId };
      currentRef.current = merged;
      setBoletimState(merged);
      if (!hasNewerLocalChanges) showStatus("synced");
      else showStatus("pending");
      dirtyRef.current = hasNewerLocalChanges;
    }
    const records = offlineDraftsEnabled ? await getPendingDrafts(currentUserId()) : [];
    setPendingCount(records.length);
    return nextRecord;
  }, [showStatus]);

  const sendSnapshot = useCallback(async (report, localId, version, options = {}) => {
    if (!localStorage.getItem("x-access-token")) {
      showStatus("auth-required", "Entre novamente para sincronizar os rascunhos pendentes.");
      return { report, synced: false, reason: "auth" };
    }
    if (syncingRef.current) {
      if (!options.waitForBusy) return { report, synced: false, reason: "busy" };
      await new Promise((resolve) => syncWaitersRef.current.push(resolve));
      return sendSnapshot(currentRef.current, localIdRef.current, versionRef.current, { waitForBusy: true });
    }
    syncingRef.current = true;
    showStatus("syncing");
    try {
      let mutation = options.mutation || null;
      if (!mutation && offlineDraftsEnabled) {
        const stored = await getDraft(localId);
        mutation = stored?.inFlightMutation || null;
      }
      const payload = mutation ? { ...mutation.payload } : { ...report, clientRequestId: report.clientRequestId || uuidv4() };
      delete payload.formDrafts;
      const mutationVersion = mutation?.version ?? version;
      if (payload._id) {
        payload.expectedRevision = Number(payload.documentRevision || 0);
        payload.syncRequestId = mutation?.syncRequestId || `${payload.clientRequestId}:${mutationVersion}`;
      }
      mutation = { version: mutationVersion, payload, syncRequestId: payload.syncRequestId || null };
      if (offlineDraftsEnabled) await markDraftInFlight(localId, mutation);
      const response = await api.post("/incidentReport/create", { incidentReport: payload });
      const saved = response.data.incidentReport;
      if (!saved?._id) throw new Error('O servidor não confirmou a identificação do boletim.');
      retryAttemptsRef.current = 0;
      window.clearTimeout(retryRef.current);
      retryRef.current = null;
      await applyServerReport(saved, localId, mutationVersion, mutation.syncRequestId);
      if (offlineDraftsEnabled) {
        const latest = await getDraft(localId);
        if (latest?.status === "pending") window.setTimeout(() => syncPendingRef.current(), 100);
      }
      return { report: saved, synced: true };
    } catch (error) {
      if (error.response?.status === 409) {
        if (offlineDraftsEnabled) {
          const record = await getDraft(localId);
          if (record) await putDraftPreservingInFlight({ ...record, status: "conflict", erro: error.response.data?.message || "O boletim mudou no servidor." });
        }
        showStatus("conflict", error.response?.data?.message || "O boletim foi alterado em outro local. Compare as versões antes de continuar.");
        return { report, synced: false, permanent: true, reason: "conflict", error };
      }
      if (isPermanentError(error)) {
        // Rejected mutations must not prevent synchronization after correction.
        if (offlineDraftsEnabled && ![401, 403].includes(error.response?.status)) await markDraftInFlight(localId, null);
        const message = error.response?.data?.message || "O servidor rejeitou os dados do boletim.";
        showStatus(error.response?.status === 401 ? "auth-required" : "error", message);
        return { report, synced: false, permanent: true, reason: error.response?.status === 401 ? "auth" : "validation", error };
      }
      showStatus("waiting", offlineDraftsEnabled ? "Salvo neste dispositivo; aguardando o backend." : "Sem confirmação do servidor; mantenha esta página aberta para tentar novamente.");
      return { report, synced: false, reason: "connection", error };
    } finally {
      syncingRef.current = false;
      syncWaitersRef.current.splice(0).forEach((resolve) => resolve());
    }
  }, [applyServerReport, showStatus]);

  const scheduleRetry = useCallback(() => {
    if (retryRef.current) return;
    const delay = Math.min(60000, 3000 * (2 ** Math.min(retryAttemptsRef.current, 4)));
    retryAttemptsRef.current += 1;
    retryRef.current = window.setTimeout(() => {
      retryRef.current = null;
      syncPendingRef.current();
    }, delay);
  }, []);

  const queueSync = useCallback((report, localId, version, delay = 900) => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      if (localId !== localIdRef.current || version !== versionRef.current || !reportIsReady(report)) return;
      try {
        await persistLocally(report, "pending", version);
        const result = await sendSnapshot(report, localId, version);
        if (result.permanent && result.reason !== "auth" && offlineDraftsEnabled) {
          const record = await getDraft(localId);
          if (record) await putDraftPreservingInFlight({
            ...record,
            status: result.reason === "conflict" ? "conflict" : "error",
            erro: result.error?.response?.data?.message || syncStatus.message || "Falha permanente ao sincronizar.",
          });
        }
        if (!result.synced && !result.permanent) scheduleRetry();
        if (offlineDraftsEnabled) {
          const pending = await getPendingDrafts(currentUserId());
          setPendingCount(pending.length);
        }
      } catch (error) {
        showStatus("error", error.message || "Não foi possível preservar o rascunho neste dispositivo.");
      }
    }, delay);
  }, [persistLocally, scheduleRetry, sendSnapshot, showStatus, syncStatus.message]);

  const syncPending = useCallback(async () => {
    const userId = currentUserId();
    if (!userId || !localStorage.getItem("x-access-token") || syncingRef.current) return;
    if (!offlineDraftsEnabled) {
      if (!dirtyRef.current) return;
      const report = currentRef.current;
      if (reportIsReady(report)) await sendSnapshot(report, localIdRef.current, versionRef.current);
      return;
    }
    try {
      const pending = await getPendingDrafts(userId);
      setPendingCount(pending.length);
      for (const record of pending) {
        if (!reportIsReady(record.dados)) continue;
        const result = await sendSnapshot(record.dados, record.localId, record.versaoLocal);
        if (!result.synced && !result.permanent) {
          await putDraftPreservingInFlight({ ...record, status: "pending", tentativas: (record.tentativas || 0) + 1, erro: result.error?.message || "Backend indisponível", ultimaTentativa: new Date().toISOString() });
          scheduleRetry();
          break;
        }
        if (result.permanent && result.reason !== "conflict" && result.reason !== "auth") {
          await putDraftPreservingInFlight({ ...record, status: "error", erro: result.error?.response?.data?.message || "Erro permanente de validação" });
        }
      }
      const remaining = await getPendingDrafts(userId);
      setPendingCount(remaining.length);
    } catch (error) {
      showStatus("error", error.message || "Não foi possível consultar a fila de sincronização.");
      scheduleRetry();
    }
  }, [scheduleRetry, sendSnapshot, showStatus]);

  syncPendingRef.current = syncPending;

  const setBoletim = useCallback((valueOrUpdater) => {
    const previous = currentRef.current;
    const requested = typeof valueOrUpdater === "function" ? valueOrUpdater(previous) : valueOrUpdater;
    if (requested === previous) return;
    const switchedServerReport = requested?._id && String(requested._id) !== String(previous?._id || "");
    const startingNewReport = !requested?._id && (Boolean(previous?._id) ||
      (!requested?.clientRequestId && hasContent(previous) && isBlankDraft(requested)));
    if (switchedServerReport) {
      window.clearTimeout(debounceRef.current);
      dirtyRef.current = false;
      finalizationIntentRef.current = null;
      localIdRef.current = `server:${requested._id}`;
      versionRef.current = Number(requested.documentRevision || 0);
    } else if (startingNewReport) {
      window.clearTimeout(debounceRef.current);
      localIdRef.current = uuidv4();
      versionRef.current = 0;
      finalizationIntentRef.current = null;
    } else if (!localIdRef.current) {
      localIdRef.current = uuidv4();
    }
    const stableClientId = requested._id
      ? requested.clientRequestId || uuidv4()
      : startingNewReport ? uuidv4() : requested.clientRequestId || previous.clientRequestId || uuidv4();
    const next = { ...requested, clientRequestId: stableClientId };
    currentRef.current = next;
    setBoletimState(next);

    if (switchedServerReport) {
      persistLocally(next, "synced", versionRef.current).catch((error) => showStatus("error", error.message));
      showStatus("synced");
      return;
    }

    if (!next._id && isBlankDraft(next)) {
      dirtyRef.current = false;
      showStatus("idle");
      return;
    }

    versionRef.current += 1;
    dirtyRef.current = true;
    showStatus("pending");
    const savedVersion = versionRef.current;
    const savedLocalId = localIdRef.current;
    persistLocally(next, "pending", savedVersion)
      .then(async () => {
        if (offlineDraftsEnabled && localIdRef.current === savedLocalId && versionRef.current === savedVersion) {
          showStatus("saved-local");
        }
        if (offlineDraftsEnabled) {
          const pending = await getPendingDrafts(currentUserId());
          setPendingCount(pending.length);
        }
      })
      .catch((error) => showStatus("error", error.message || "Falha ao salvar no dispositivo."));
    if (reportIsReady(next)) queueSync(next, localIdRef.current, versionRef.current);
  }, [persistLocally, queueSync, showStatus]);

  const saveNowImpl = useCallback(async ({ finalizationIntent = false } = {}) => {
    window.clearTimeout(debounceRef.current);
    const snapshot = currentRef.current;
    if (snapshot._id && !dirtyRef.current) return { report: snapshot, synced: true };
    const localId = localIdRef.current;
    const version = versionRef.current;
    let persistedLocally = false;
    try {
      persistedLocally = Boolean(await persistLocally(snapshot, "pending", version));
      if (finalizationIntent && offlineDraftsEnabled) {
        finalizationIntentRef.current = finalizationIntentRef.current || new Date().toISOString();
        await persistLocally(snapshot, "pending", version);
      }
    } catch (error) {
      showStatus("error", error.message || "O rascunho não pôde ser preservado no dispositivo.");
      if (!navigator.onLine) throw error;
    }
    if (!reportIsReady(snapshot)) {
      return { report: snapshot, synced: false, permanent: true, error: boletimValidationError(snapshot) };
    }
    let result = await sendSnapshot(snapshot, localId, version, { waitForBusy: true });
    // An idempotent replay can confirm an earlier offline mutation. Flush the
    // latest version before permitting an official PDF or reporting success.
    for (let attempt = 0; result.synced && dirtyRef.current && localIdRef.current === localId && attempt < 5; attempt += 1) {
      const validationError = boletimValidationError(currentRef.current);
      if (validationError) return { synced: false, error: validationError };
      await persistLocally(currentRef.current, 'pending', versionRef.current);
      result = await sendSnapshot(currentRef.current, localId, versionRef.current, { waitForBusy: true });
    }
    if (result.synced && (dirtyRef.current || localIdRef.current !== localId)) {
      return { synced: false, error: new Error('O formulário mudou durante o salvamento. Aguarde e salve novamente antes de imprimir.') };
    }
    if (result.synced) return { report: result.report, synced: true };
    if (result.permanent) return { report: snapshot, synced: false, error: result.error || new Error(syncStatus.message || "Falha ao registrar o boletim.") };
    if (!offlineDraftsEnabled) {
      return { report: snapshot, synced: false, error: new Error("O modo de rascunho offline não está habilitado; o boletim não foi preservado localmente.") };
    }
    if (!persistedLocally) return { report: snapshot, synced: false, error: new Error("Não foi possível confirmar a gravação local; a impressão offline foi bloqueada para evitar perda do boletim.") };
    return { report: { ...snapshot, _offlinePending: true }, synced: false, offline: true };
  }, [persistLocally, sendSnapshot, showStatus, syncStatus.message]);

  const saveNow = useCallback((options = {}) => {
    if (!savePromiseRef.current) {
      savePromiseRef.current = saveNowImpl(options).finally(() => { savePromiseRef.current = null; });
    }
    return savePromiseRef.current;
  }, [saveNowImpl]);

  const salvarBoletim = useCallback(async () => {
    const result = await saveNow();
    if (result.error) throw result.error;
    return result.report;
  }, [saveNow]);

  const flushLocalDraft = useCallback(async () => {
    if (!dirtyRef.current) return true;
    if (!offlineDraftsEnabled) {
      throw new Error("A atualização foi pausada porque este build não pode preservar alterações locais offline.");
    }
    await persistLocally(currentRef.current, syncStatus.state === "synced" ? "synced" : "pending", versionRef.current);
    return true;
  }, [persistLocally, syncStatus.state]);

  const resetBoletim = useCallback(() => {
    window.clearTimeout(debounceRef.current);
    localIdRef.current = uuidv4();
    versionRef.current = 0;
    dirtyRef.current = false;
    finalizationIntentRef.current = null;
    const empty = novoBoletimVazio();
    currentRef.current = empty;
    setBoletimState(empty);
    showStatus("idle");
  }, [showStatus]);

  const discardCurrentDraft = useCallback(async () => {
    if (offlineDraftsEnabled && currentUserId()) await deleteDraft(localIdRef.current);
    resetBoletim();
    setPendingCount(offlineDraftsEnabled ? (await getPendingDrafts(currentUserId())).length : 0);
  }, [resetBoletim]);

  const carregarRascunho = useCallback(async () => {
    const userId = currentUserId();
    setPendingCount(0);
    showStatus("idle");
    if (!userId || !offlineDraftsEnabled) {
      const empty = novoBoletimVazio();
      currentRef.current = empty;
      localIdRef.current = empty.clientRequestId;
      versionRef.current = 0;
      dirtyRef.current = false;
      finalizationIntentRef.current = null;
      setBoletimState(empty);
      setPendingCount(0);
      showStatus("idle");
      return false;
    }
    try {
      await migrateLegacyDraft(userId, uuidv4).catch((error) => {
        showStatus("error", error.message);
        return null;
      });
      const record = await getLatestDraft(userId);
      if (!record) {
        const empty = novoBoletimVazio();
        currentRef.current = empty;
        localIdRef.current = empty.clientRequestId;
        versionRef.current = 0;
        dirtyRef.current = false;
        finalizationIntentRef.current = null;
        setBoletimState(empty);
        setPendingCount(0);
        showStatus("idle");
        return false;
      }
      localIdRef.current = record.localId;
      versionRef.current = record.versaoLocal || 0;
      dirtyRef.current = record.status !== "synced";
      finalizationIntentRef.current = record.finalizationRequestedAt || null;
      currentRef.current = record.dados;
      setBoletimState(record.dados);
      showStatus(record.status === "synced" ? "synced" : record.status === "conflict" ? "conflict" : record.status === "error" ? "error" : "saved-local", record.erro || "");
      const pending = await getPendingDrafts(userId);
      setPendingCount(pending.length);
      if (pending.length) syncPending();
      return true;
    } catch (error) {
      showStatus("error", error.message || "Não foi possível recuperar o rascunho local.");
      return false;
    }
  }, [showStatus, syncPending]);

  useEffect(() => {
    mountedRef.current = true;
    if (!offlineDraftsEnabled) return () => { mountedRef.current = false; };
    const onOnline = () => syncPending();
    const onFocus = () => syncPending();
    const onVisibility = () => { if (document.visibilityState === "visible") syncPending(); };
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    if (currentUserId()) syncPending();
    return () => {
      mountedRef.current = false;
      window.clearTimeout(debounceRef.current);
      window.clearTimeout(retryRef.current);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [syncPending]);

  useEffect(() => {
    if (offlineDraftsEnabled && currentUserId()) carregarRascunho();
  }, [carregarRascunho]);

  return (
    <BoletimContext.Provider value={{
      boletim,
      setBoletim,
      salvarBoletim,
      saveNow,
      flushLocalDraft,
      resetBoletim,
      discardCurrentDraft,
      carregarRascunho,
      syncPending,
      syncStatus,
      pendingCount,
      offlineDraftsEnabled,
    }}>
      {children}
    </BoletimContext.Provider>
  );
}

export { BoletimContext, BoletimProvider, novoBoletimVazio };
